import { Nav, NavItem, NavLink } from 'reactstrap'
import classnames from 'classnames'
import React from 'react'
import SupportMerchantApiDto from '../../../../api-dtos/support/merchant/support-merchant-api.dto.interface'
import KYBStatusPill from '../../support-merchant-kyb/components/kyb-status-pill/kyb-status-pill.component'

type NavTabItemProps = {
  tab: MerchantTab
  label: string
  activeTab: MerchantTab
  toggleTab: (tab: MerchantTab) => void
  badge?: React.ReactNode
}

const NavTabItem: React.FC<NavTabItemProps> = ({
  tab,
  label,
  activeTab,
  toggleTab,
  badge,
}) => (
  <NavItem>
    <NavLink
      style={{ cursor: 'pointer' }}
      className={classnames({ active: activeTab === tab })}
      onClick={() => toggleTab(tab)}
    >
      {label}
      {badge && <div className="float-end">{badge}</div>}
    </NavLink>
  </NavItem>
)

export type MerchantTab =
  | 'kyc'
  | 'kyb'
  | 'recentActivity'
  | 'onboarding'
  | 'profile'
  | 'schedules'
  | 'cashDiscounts'
  | 'hardware'
  | 'payments'
  | 'paymentLinks'
  | 'rewards'
  | 'lopayCard'
  | 'payouts'
  | 'referrals'
  | 'charges'
  | 'disputes'
  | 'quickbooks'
  | 'workingCapital'
  | 'cashAdvances'
  | 'riskReserves'
  | 'team'
  /** Tab names below here are disabled */
  | 'fees'
  | 'sales'
  | 'bankAccounts'
  | 'products'
  | 'openBankingVrpConsents'

type MerchantPageNavbarProps = {
  activeTab: MerchantTab
  toggleTab: (tab: MerchantTab) => void
  merchant: SupportMerchantApiDto
}

/**
 * Component handling the rending and display of the merchant page vertical navigation bar.
 *
 * @param activeTab - The tab that should be highlighted.
 * @param toggleTab - The function called when toggling/selecting a new tab.
 * @param merchant - The merchant whose page this navbar is on.
 */
const MerchantPageNavbar = ({
  activeTab,
  toggleTab,
  merchant,
}: MerchantPageNavbarProps) => {
  return (
    <Nav pills className="flex-column">
      <NavTabItem
        tab="kyb"
        label="KYB"
        activeTab={activeTab}
        toggleTab={toggleTab}
        badge={<KYBStatusPill kybStatus={merchant.kybStatus} />}
      />
      <NavTabItem
        tab="recentActivity"
        label="KYB Activity & Notes"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="onboarding"
        label="Onboarding progress"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="profile"
        label="Profile"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="schedules"
        label="Manage Schedule & Audit Log"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      {merchant.country === 'US' && (
        <NavTabItem
          tab="cashDiscounts"
          label="Cash Discounts"
          activeTab={activeTab}
          toggleTab={toggleTab}
        />
      )}
      <NavTabItem
        tab="hardware"
        label="Hardware"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="payments"
        label="Payments"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="paymentLinks"
        label="Payment Links"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="rewards"
        label="Rewards"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="lopayCard"
        label="Lopay Card"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="payouts"
        label="Payouts"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="referrals"
        label="Referrals &amp; Discounts"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="charges"
        label="Charges"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="disputes"
        label="Disputes"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="riskReserves"
        label="Risk Reserves"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="quickbooks"
        label="Quickbooks"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="workingCapital"
        label="Working Capital"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="cashAdvances"
        label="Cash Advances"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      <NavTabItem
        tab="team"
        label="Team"
        activeTab={activeTab}
        toggleTab={toggleTab}
      />
      {1 + 1 === 1 && (
        <>
          <NavTabItem
            tab="fees"
            label="Fees"
            activeTab={activeTab}
            toggleTab={toggleTab}
          />
          <NavTabItem
            tab="sales"
            label="Sales"
            activeTab={activeTab}
            toggleTab={toggleTab}
          />
          <NavTabItem
            tab="bankAccounts"
            label="Bank Accounts"
            activeTab={activeTab}
            toggleTab={toggleTab}
          />
          <NavTabItem
            tab="products"
            label="POS Products"
            activeTab={activeTab}
            toggleTab={toggleTab}
          />
        </>
      )}
      {/* Hide in production until ready */}
      {1 + 1 === 1 && (
        <NavTabItem
          tab="openBankingVrpConsents"
          label="VRP Consents"
          activeTab={activeTab}
          toggleTab={toggleTab}
        />
      )}
    </Nav>
  )
}

export default MerchantPageNavbar
